import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

// Props interface for ErrorBoundary component
// children: The components to protect from errors
// fallback: Optional custom error display instead of default error page
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

// State interface for tracking error information
// hasError: Whether an error has occurred
// error: The actual error that happened
// errorInfo: Additional details about where the error occurred
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary Component
 * 
 * This component acts like a safety net for the entire app. When any part of the app
 * crashes due to a programming error, this component catches it and shows a friendly
 * error message instead of a blank white screen.
 * 
 * Think of it like an airbag in a car - it only activates when something goes wrong,
 * but when it does, it prevents a much worse experience for the user.
 * 
 * How it works:
 * 1. Wraps other components and monitors them for errors
 * 2. If any child component crashes, catches the error
 * 3. Logs the error details for debugging
 * 4. Shows a user-friendly error message with options to recover
 * 5. Provides a "Try Again" button to attempt to recover
 * 6. Provides a "Go Home" button as a safe fallback
 * 
 * Note: This only catches errors in React components, not in event handlers
 * or asynchronous code like setTimeout or API calls.
 */
class ErrorBoundary extends Component<Props, State> {
  // Initialize the component with no error state
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  // This static method is called when an error occurs in any child component
  // It updates the state to indicate an error has happened
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  // This method is called after an error has been caught
  // It's where we can log the error or send it to an error reporting service
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to the console for debugging
    console.error('Error caught by boundary:', error, errorInfo);
    
    // Store the error details in state so we can display them if needed
    this.setState({ error, errorInfo });
    
    // In a production app, you might send this error to a monitoring service
    // logErrorToService(error, errorInfo);
  }

  // Handler for the "Try Again" button - resets the error state
  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  // The main render method that decides what to show
  render() {
    // If an error has occurred, show the error UI
    if (this.state.hasError) {
      // If a custom fallback was provided, use that instead
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Otherwise, show our default error page
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            {/* Error icon and title */}
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">
                Something went wrong
              </h1>
            </div>
            
            {/* User-friendly error message */}
            <p className="text-gray-600 mb-4">
              An unexpected error occurred. The error has been logged and we'll look into it.
            </p>
            
            {/* Show technical error details only in development mode */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error details (development only)
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            
            {/* Recovery options */}
            <div className="flex gap-3">
              {/* Try to reload the component that crashed */}
              <button
                onClick={this.handleReset}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              {/* Go back to the home page as a safe fallback */}
              <button
                onClick={() => window.location.href = '/'}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    // No error occurred, so just render the child components normally
    return this.props.children;
  }
}

export default ErrorBoundary;